import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/20/solid";
import { t } from "i18next";

// Definir el tipo para los props del componente
export type PaginationProps = {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void; // Función que se ejecuta al cambiar de página
  pageSize: number;
  totalResults: number;
  pageRange?: number; // Prop opcional para controlar cuántas páginas se muestran
};

const PaginationUI: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  pageSize,
  totalResults,
  pageRange = 5, // Valor predeterminado para el rango de páginas visibles
}) => {
  const startResult = (currentPage - 1) * pageSize + 1;
  const endResult = Math.min(currentPage * pageSize, totalResults);

  const handlePreviousClick = () => {
    if (currentPage > 1) onPageChange(currentPage - 1);
  };

  const handleNextClick = () => {
    if (currentPage < totalPages) onPageChange(currentPage + 1);
  };

  const getPageNumbers = () => {
    const pages = [];
    const halfRange = Math.floor(pageRange / 2);
    let startPage = Math.max(1, currentPage - halfRange);
    let endPage = Math.min(totalPages, currentPage + halfRange);

    if (currentPage - halfRange < 1) {
      endPage = Math.min(totalPages, endPage + (halfRange - (currentPage - 1)));
    }

    if (currentPage + halfRange > totalPages) {
      startPage = Math.max(
        1,
        startPage - (halfRange - (totalPages - currentPage))
      );
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  };

  return (
    <div className="flex flex-col space-y-4 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:space-y-0 sm:space-x-4 sm:px-6">
      {/* Results Text */}
      <div className="text-sm text-gray-700">
        {t("general.pagination.showing")}{" "}
        <span className="font-medium">{startResult}</span>{" "}
        {t("general.pagination.to")}{" "}
        <span className="font-medium">{endResult}</span>{" "}
        {t("general.pagination.of")}{" "}
        <span className="font-medium">{totalResults}</span>{" "}
        <span className="lowercase">{t("general.pagination.results")}</span>
      </div>

      {/* Pagination Controls */}
      <div>
        {/* Mobile View: Previous/Next Buttons */}
        <div className="flex justify-center space-x-3 sm:hidden">
          <button
            onClick={handlePreviousClick}
            disabled={currentPage === 1}
            className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            {t("general.pagination.previous")}
          </button>
          <button
            onClick={handleNextClick}
            disabled={currentPage === totalPages}
            className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            {t("general.pagination.next")}
          </button>
        </div>

        {/* Desktop View: Full Pagination */}
        <nav
          aria-label="Pagination"
          className="hidden sm:isolate sm:inline-flex sm:-space-x-px sm:rounded-md"
        >
          <button
            onClick={handlePreviousClick}
            disabled={currentPage === 1}
            className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50"
          >
            <span className="sr-only">{t("general.pagination.previous")}</span>
            <ChevronLeftIcon aria-hidden="true" className="h-5 w-5" />
          </button>
          {getPageNumbers().map((page) => (
            <button
              key={page}
              onClick={() => onPageChange(page)}
              aria-current={page === currentPage ? "page" : undefined}
              className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                page === currentPage
                  ? "bg-primary text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                  : "text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:outline-offset-0"
              }`}
            >
              {page}
            </button>
          ))}
          <button
            onClick={handleNextClick}
            disabled={currentPage === totalPages}
            className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50"
          >
            <span className="sr-only">{t("general.pagination.next")}</span>
            <ChevronRightIcon aria-hidden="true" className="h-5 w-5" />
          </button>
        </nav>
      </div>
    </div>
  );
};

export default PaginationUI;
